/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2023 (original work) Open Assessment Technologies SA ;
 */
import { ResizeObserver } from '../polyfills.js';

const callbacks = new WeakMap();
export const resizeObserver = new ResizeObserver((entries, observer) => {
    for (const entry of entries) {
        const entryCallbacks = callbacks.get(entry.target);
        if (!entryCallbacks) {
            return;
        }
        for (const entryCallback of entryCallbacks) {
            entryCallback(entry, observer);
        }
    }
});

/**
 * @param {Element} node
 * @param {Object} [options]
 * @param {(entry: ResizeObserverEntry, observer: ResizeObserver) => {}} [options.callback]
 * @param {Object} [options.observeOptions] - see https://developer.mozilla.org/en-US/docs/Web/API/ResizeObserver/observe#options
 * @returns {Object}
 */
export default function useResizeObserver(node, options = {}) {
    const { callback = () => {}, observeOptions } = options;

    let nodeCallbacks = callbacks.get(node);
    if (!nodeCallbacks) {
        callbacks.set(node, new Set());
        nodeCallbacks = callbacks.get(node);
    }
    nodeCallbacks.add(callback);

    resizeObserver.observe(node, observeOptions);

    return {
        destroy() {
            resizeObserver.unobserve(node);
            nodeCallbacks.delete(callback);
            if (nodeCallbacks.size < 1) {
                callbacks.delete(node);
            }
        }
    };
}